import { CameraProjection } from '@skand/three-js-renderer';
import { Ellipsoid } from '@skand/three-js-renderer/bin/private/math';
import { Matrix4, Quaternion, Vector3 } from 'three';
import { toCartographic } from '../../../Utils';
/**
 * Base class for all navigation modes and camera controls.
 *
 * Below are the universal controls for all modes
 *
 * Scroll - Zoom in or out
 * 'WASD' - Move the camera around the world in first-person mode
 * 'QE' - Move the camera up and down (relative to the globe surface)
 */
export class BaseCameraMode {
    constructor(renderer, gestures) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "gestures", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "camera", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "globe", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "underground", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isRotating", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cameraRotationPivot", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pitchRange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pitch", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "yaw", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "targetPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "targetRotation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lastTargetPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lastTargetRotation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "prevPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "prevRotation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "moveStates", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cameraMotionCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cameraStopCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stopped", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.renderer = renderer;
        this.gestures = gestures;
        this.camera = renderer.getWorld().getCamera();
        this.globe = renderer.getWorld().getGlobe();
        this.underground = false;
        this.isRotating = false;
        this.cameraRotationPivot = new Vector3();
        this.pitchRange = Math.PI - 0.05;
        this.pitch = 0;
        this.yaw = 0;
        this.targetPosition = null;
        this.targetRotation = null;
        this.lastTargetPosition = new Vector3();
        this.lastTargetRotation = new Quaternion();
        this.camera.getPositionECEF(this.lastTargetPosition);
        this.camera.getRotation(this.lastTargetRotation);
        this.prevPosition = new Vector3();
        this.prevRotation = new Quaternion();
        this.camera.getPositionECEF(this.prevPosition);
        this.camera.getRotation(this.prevRotation);
        this.moveStates = {
            forward: false,
            backward: false,
            up: false,
            down: false,
            strafeLeft: false,
            strafeRight: false,
            speedUp: false,
        };
        this.stopped = false;
        this.cameraMotionCallback = () => { };
        this.cameraStopCallback = () => { };
    }
    /**
     * Apply transformation matrix
     */
    clampHeight() {
        const cartographic = this.camera.getPositionCartographic();
        const terrainHeight = Math.min(this.globe.getTerrainHeight(cartographic), 20000);
        if (cartographic.height <= terrainHeight + 0.5) {
            this.pitch = 0;
            cartographic.height = terrainHeight + 0.5;
            this.camera.setPositionCartographic(cartographic);
        }
    }
    /**
     * Clamp the pitch angle value
     */
    clampPitch() {
        const pitchMin = -(this.pitchRange / 2);
        const pitchMax = pitchMin + this.pitchRange;
        this.pitch = Math.max(Math.min(this.pitch, pitchMax), pitchMin);
    }
    /**
     * Handle camera rotation
     */
    applyTransforms() {
        if (!this.underground) {
            this.clampHeight();
        }
        this.clampPitch();
        const position = new Vector3();
        this.camera.getPositionECEF(position);
        const rotation = new Quaternion();
        this.camera.getRotation(rotation);
        const up = new Vector3();
        const tangent = new Vector3();
        const bitangent = new Vector3();
        Ellipsoid.WGS_84.rotationBasisForCartographic(toCartographic(position), null, up, tangent, bitangent);
        this.camera.setUpVector(up);
        const direction = tangent
            .clone()
            .applyAxisAngle(bitangent, this.pitch)
            .applyAxisAngle(up, this.yaw)
            .add(position);
        this.camera.lookAt(direction);
        this.cameraMotionCallback(position, rotation);
    }
    /**
     * Update pitch and yaw given the current configuration of the camera
     */
    updateRotationFromCamera() {
        // Calculate applied rotation axes
        const position = new Vector3();
        const rotation = new Quaternion();
        this.camera.getPositionECEF(position);
        this.camera.getRotation(rotation);
        const x = new Vector3(); // Camera right
        const y = new Vector3(); // Camera up
        const z = new Vector3(); // Camera forward
        new Matrix4().makeRotationFromQuaternion(rotation).extractBasis(x, y, z);
        const up = new Vector3(); // Globe surface normal
        const bitangent = new Vector3();
        Ellipsoid.WGS_84.rotationBasisForCartographic(toCartographic(position), null, up, undefined, bitangent);
        const projected = x.projectOnPlane(up);
        const crossed = new Vector3().crossVectors(bitangent, projected);
        this.yaw = projected.angleTo(bitangent) * Math.sign(up.dot(crossed));
        this.pitch = -y.angleTo(up);
        this.applyTransforms();
    }
    /**
     * Calculate the target distance from the camera to a point picked from world space
     */
    calculateTargetDistance(worldPick) {
        const currentPosition = new Vector3();
        this.camera.getPositionECEF(currentPosition);
        const distanceCap = 250;
        const clipDistance = this.camera.getFarPlane();
        const displacement = worldPick.clone().sub(currentPosition);
        const refLength = displacement.length();
        const normal = displacement.clone().divideScalar(refLength);
        // Calculate intersection point with globe (even though globe is not visible)
        const { x, y, z } = Ellipsoid.WGS_84.radii;
        const x0 = currentPosition.x;
        const y0 = currentPosition.y;
        const z0 = currentPosition.z;
        const x1 = displacement.x;
        const y1 = displacement.y;
        const z1 = displacement.z;
        const a = Math.pow(x1 / x, 2) + Math.pow(y1 / y, 2) + Math.pow(z1 / z, 2);
        const b = 2 * ((x0 * x1) / (x * x) + (y0 * y1) / (y * y) + (z0 * z1) / (z * z));
        const c = Math.pow(x0 / x, 2) + Math.pow(y0 / y, 2) + Math.pow(z0 / z, 2) - 1;
        // Calculate the determinant
        // D < 0 means no intersection points, so we are zooming into empty space
        const d = b * b - 4 * a * c;
        if (d < 0) {
            return Math.min(distanceCap, refLength);
        }
        // Handle the underground case separately
        if (this.underground && toCartographic(currentPosition).height < 1000) {
            if (refLength >= clipDistance) {
                return distanceCap;
            }
            else {
                return refLength;
            }
        }
        // Calculate the intersection points with the virtual globe
        const t0 = (-b - Math.sqrt(d)) / (2 * a);
        const t1 = (-b + Math.sqrt(d)) / (2 * a);
        const p0 = displacement.clone().multiplyScalar(t0);
        const p1 = displacement.clone().multiplyScalar(t1);
        // Pick the closest point in front of the camera
        let targetDistance = refLength;
        if (normal.dot(p0) >= 0) {
            targetDistance = Math.min(targetDistance, p0.length());
        }
        if (normal.dot(p1) >= 0) {
            targetDistance = Math.min(targetDistance, p1.length());
        }
        if (targetDistance >= clipDistance) {
            return distanceCap;
        }
        else {
            return targetDistance;
        }
    }
    /**
     * Handle keyboard press
     */
    onKeyDown(key) {
        if (key === 'w' || key === 'W') {
            this.moveStates.forward = true;
        }
        if (key === 's' || key === 'S') {
            this.moveStates.backward = true;
        }
        if (key === 'a' || key === 'A') {
            this.moveStates.strafeLeft = true;
        }
        if (key === 'd' || key === 'D') {
            this.moveStates.strafeRight = true;
        }
        if (key === 'q' || key === 'Q') {
            this.moveStates.down = true;
        }
        if (key === 'e' || key === 'E') {
            this.moveStates.up = true;
        }
        if (key === ' ') {
            this.moveStates.speedUp = true;
        }
    }
    /**
     * Handle keyboard release
     */
    onKeyRelease(key) {
        if (key === 'w' || key === 'W') {
            this.moveStates.forward = false;
        }
        if (key === 's' || key === 'S') {
            this.moveStates.backward = false;
        }
        if (key === 'a' || key === 'A') {
            this.moveStates.strafeLeft = false;
        }
        if (key === 'd' || key === 'D') {
            this.moveStates.strafeRight = false;
        }
        if (key === 'q' || key === 'Q') {
            this.moveStates.down = false;
        }
        if (key === 'e' || key === 'E') {
            this.moveStates.up = false;
        }
        if (key === ' ') {
            this.moveStates.speedUp = false;
        }
    }
    /**
     * Implements the camera pivot behavior on right-click drag
     */
    pivotCamera(screenPosition, prevPosition) {
        // Rotate camera around its center (orientation)
        const diffX = prevPosition.x - screenPosition.x;
        const diffY = prevPosition.y - screenPosition.y;
        const angularScale = 0.005;
        this.pitch += angularScale * diffY;
        this.yaw += angularScale * diffX;
        // Rotate camera position around selected pivot
        const pivot = this.cameraRotationPivot.clone();
        const cameraPosition = new Vector3();
        this.camera.getPositionECEF(cameraPosition);
        const offset = cameraPosition.sub(pivot);
        // Vertical axis is the normal vector on the ellpsoid at the pivot
        const vertical = new Vector3();
        Ellipsoid.WGS_84.geodeticSurfaceNormalForPosition(pivot, vertical);
        // Horizontal axis for pitch rotation is parallel to the x-axis of the viewport
        const worldMatrix = new Matrix4();
        this.camera.getWorldMatrix(worldMatrix);
        const horizontal = new Vector3();
        worldMatrix.extractBasis(horizontal, new Vector3(), new Vector3());
        if (Math.abs(this.pitch) <= this.pitchRange / 2) {
            offset.applyAxisAngle(horizontal, angularScale * diffY);
        }
        offset.applyAxisAngle(vertical, angularScale * diffX);
        const newPosition = offset.add(pivot);
        this.camera.setPositionECEF(newPosition);
    }
    /**
     * Handle screen press event (either click or tap)
     */
    onPress(screenPosition) {
        const pick = this.renderer.pickScreenWorldSpaceSnapping(screenPosition);
        const worldPick = new Vector3(pick.position.x, pick.position.y, pick.position.z);
        const cameraPosition = new Vector3();
        this.camera.getPositionECEF(cameraPosition);
        // Set translate start position
        const direction = worldPick.clone().sub(cameraPosition).normalize();
        const targetDistance = this.calculateTargetDistance(worldPick);
        const targetPosition = cameraPosition.add(direction.multiplyScalar(targetDistance));
        this.cameraRotationPivot.x = targetPosition.x;
        this.cameraRotationPivot.y = targetPosition.y;
        this.cameraRotationPivot.z = targetPosition.z;
        this.handleCameraActivate(screenPosition);
    }
    /**
     * Handle screen drag event
     */
    onDrag(screenPosition, prevPosition) {
        if (this.isRotating) {
            this.pivotCamera(screenPosition, prevPosition);
        }
        this.handleCameraDrag(screenPosition, prevPosition);
        this.applyTransforms();
    }
    /**
     * Handle screen release event
     */
    onRelease(screenPosition) {
        this.isRotating = false;
        this.handleCameraRelease(screenPosition);
    }
    /**
     * Handle zoom event
     *
     * This will be used to control zoom
     */
    onZoom(screenPosition, direction) {
        // Handle orthographic case specially
        if (this.camera.getProjectionMode() === CameraProjection.Orthographic) {
            const orthographic = this.camera.getOrthographic();
            // Apply logarithmic scaling
            let logScale = Math.log(orthographic.getZoom());
            logScale -= Math.sign(direction) * 0.05;
            orthographic.setZoom(Math.exp(logScale));
            return;
        }
        const pick = this.renderer.pickScreenWorldSpaceSnapping(screenPosition);
        const worldPick = new Vector3(pick.position.x, pick.position.y, pick.position.z);
        const currentPosition = new Vector3();
        this.camera.getPositionECEF(currentPosition);
        // Calculate forward and backward vectors
        const forward = new Vector3(worldPick.x, worldPick.y, worldPick.z)
            .sub(currentPosition)
            .normalize();
        const backward = forward.clone().negate();
        // Speed is determined by how far the camera from the target to be zoomed to
        const targetDistance = this.calculateTargetDistance(worldPick);
        const baseSpeed = Math.max(0.15, targetDistance * 0.05);
        const speed = this.moveStates.speedUp ? baseSpeed * 2 : baseSpeed;
        if (direction > 0) {
            const newPosition = currentPosition.add(backward.multiplyScalar(speed));
            this.camera.setPositionECEF(newPosition);
        }
        else if (direction < 0 && targetDistance > 0.25) {
            const newPosition = currentPosition.add(forward.multiplyScalar(speed));
            this.camera.setPositionECEF(newPosition);
        }
        this.applyTransforms();
        this.handleCameraZoom(screenPosition, direction);
    }
    /**
     * Update the camera position based on keyboard press state.
     */
    updateKeyboardControl() {
        const enableKeyboardControl = this.moveStates.forward ||
            this.moveStates.backward ||
            this.moveStates.up ||
            this.moveStates.down ||
            this.moveStates.strafeLeft ||
            this.moveStates.strafeRight ||
            this.moveStates.speedUp;
        if (!enableKeyboardControl)
            return;
        // Movement axes
        const worldMatrix = new Matrix4();
        this.camera.getWorldMatrix(worldMatrix);
        const right = new Vector3();
        const back = new Vector3();
        worldMatrix.extractBasis(right, new Vector3(), back);
        const up = new Vector3();
        this.camera.getUpVector(up);
        const currentPosition = new Vector3();
        this.camera.getPositionECEF(currentPosition);
        // Calculate projected distance of the camera to a wall
        const viewCenter = this.renderer.getSize();
        const pick = this.renderer.pickScreenWorldSpaceSnapping({
            x: viewCenter.x / 2,
            y: viewCenter.y / 2,
        });
        const worldPick = new Vector3(pick.position.x, pick.position.y, pick.position.z);
        const targetDistance = this.calculateTargetDistance(worldPick);
        // Forward speed (front/back) move speed depends on the distance of the camera to a wall
        const baseSpeed = Math.max(0.15, targetDistance * 0.05);
        const forwardSpeed = this.moveStates.speedUp ? baseSpeed * 2 : baseSpeed;
        const strafeSpeed = forwardSpeed;
        // Altitude (up/down) move speed is dependent on how far it is from the globe surface
        const baseAltitudeSpeed = Math.min(1.5, Math.abs(this.camera.getPositionCartographic().height * 0.05));
        const altitudeSpeed = this.moveStates.speedUp ? baseAltitudeSpeed * 2 : baseAltitudeSpeed;
        if (this.moveStates.forward && targetDistance > 0.25) {
            // Move forward
            const newPosition = currentPosition.sub(back.multiplyScalar(forwardSpeed));
            this.camera.setPositionECEF(newPosition);
            this.applyTransforms();
        }
        if (this.moveStates.backward) {
            // Move backward
            const newPosition = currentPosition.add(back.multiplyScalar(forwardSpeed));
            this.camera.setPositionECEF(newPosition);
            this.applyTransforms();
        }
        if (this.moveStates.up) {
            // Move up
            const newPosition = currentPosition.add(up.multiplyScalar(altitudeSpeed));
            this.camera.setPositionECEF(newPosition);
            this.applyTransforms();
        }
        if (this.moveStates.down) {
            // Move down
            const newPosition = currentPosition.sub(up.multiplyScalar(altitudeSpeed));
            this.camera.setPositionECEF(newPosition);
            this.applyTransforms();
        }
        if (this.moveStates.strafeLeft) {
            // Strafe left
            const newPosition = currentPosition.sub(right.multiplyScalar(strafeSpeed));
            this.camera.setPositionECEF(newPosition);
            this.applyTransforms();
        }
        if (this.moveStates.strafeRight) {
            // Strafe right
            const newPosition = currentPosition.add(right.multiplyScalar(strafeSpeed));
            this.camera.setPositionECEF(newPosition);
            this.applyTransforms();
        }
    }
    /**
     * Smoothly fly to the target position
     */
    updateFlyTo() {
        // Interpolate camera position
        if (this.targetPosition) {
            const currentPosition = new Vector3();
            this.camera.getPositionECEF(currentPosition);
            const displacement = this.targetPosition.clone().sub(currentPosition);
            const distance = displacement.length();
            const direction = displacement.multiplyScalar(1.0 / distance); // Normalize
            if (distance > 0.5) {
                const velocity = direction.clone().multiplyScalar(Math.max(1, distance / 10));
                this.camera.setPositionECEF(currentPosition.add(velocity));
            }
            else {
                this.camera.setPositionECEF(this.targetPosition);
                this.lastTargetPosition = this.targetPosition.clone();
                this.targetPosition = null;
            }
            this.applyTransforms();
        }
        // Interpolate camera rotation
        if (this.targetRotation) {
            const currentRotation = new Quaternion();
            this.camera.getRotation(currentRotation);
            const angularDisplacement = currentRotation.angleTo(this.targetRotation);
            if (Math.abs(angularDisplacement) > 0.05) {
                this.camera.setRotation(currentRotation.slerp(this.targetRotation, 0.1));
            }
            else {
                this.camera.setRotation(this.targetRotation);
                this.targetRotation = null;
            }
            this.updateRotationFromCamera();
        }
    }
    /**
     * Handle camera stop event
     */
    updateCameraStop() {
        const position = new Vector3();
        const rotation = new Quaternion();
        this.camera.getPositionECEF(position);
        this.camera.getRotation(rotation);
        if (position.distanceTo(this.prevPosition) === 0 && rotation.angleTo(this.prevRotation) === 0) {
            if (!this.stopped) {
                this.cameraStopCallback(position, rotation);
                this.stopped = true;
            }
        }
        else {
            this.stopped = false;
        }
        this.camera.getPositionECEF(this.prevPosition);
        this.camera.getRotation(this.prevRotation);
    }
    start() {
        this.stop();
        this.gestures.register('keytap', gesture => {
            this.targetPosition = null;
            this.targetRotation = null;
            this.onKeyDown(gesture.key);
        });
        this.gestures.register('keyrelease', gesture => this.onKeyRelease(gesture.key));
        this.gestures.register('tap', gesture => {
            // Rotate if:
            // * Ctrl key is pressed
            // * Right mouse button is clicked
            // * Two fingers are on the screen
            this.targetPosition = null;
            this.targetRotation = null;
            this.isRotating = gesture.ctrlKey || gesture.button === 2;
            this.onPress(gesture.position);
        });
        this.gestures.register('release', gesture => {
            this.onRelease(gesture.position);
        });
        this.gestures.register('swipe', gesture => {
            this.onDrag(gesture.position, gesture.prevPosition);
        });
        this.gestures.register('zoom', gesture => {
            this.targetPosition = null;
            this.targetRotation = null;
            this.onZoom(gesture.position, gesture.direction.y);
        });
    }
    stop() {
        this.gestures.unregisterAll();
    }
    flyTo(position, rotation) {
        this.targetPosition = position;
        this.targetRotation = rotation;
    }
    moveTo(position, rotation) {
        this.camera.setPositionECEF(position);
        this.camera.setRotation(rotation);
        this.updateRotationFromCamera();
        this.applyTransforms();
    }
    setUnderground(underground) {
        this.underground = underground;
    }
    setCameraMotionCallback(callback) {
        this.cameraMotionCallback = callback;
    }
    setCameraStopCallback(callback) {
        this.cameraStopCallback = callback;
    }
    updateFrame(deltaTime) {
        this.updateKeyboardControl();
        this.updateFlyTo();
        this.updateCameraStop();
    }
}
